import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { Button } from '@/components/ui'
import { ErrorBanner } from '@/components/ui/form'
import { useLogin } from '@/hooks/auth/useLogin'
import { AuthLayout } from './AuthLayout'
import { AuthTextField } from './AuthTextField'
import { GoogleAuthSection } from './GoogleAuthSection'

const loginSchema = z.object({
  login: z.string().min(1, 'Username or email is required'),
  password: z.string().min(1, 'Password is required'),
})

type LoginForm = z.infer<typeof loginSchema>

export default function LoginPage() {
  const [apiError, setApiError] = useState('')

  const navigate = useNavigate()
  const login = useLogin()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginForm) => {
    setApiError('')

    try {
      await login.mutateAsync({ login: data.login, password: data.password })
      navigate('/')
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Login failed')
    }
  }

  return (
    <AuthLayout title="TreasureLog" subtitle="Sign in to your account">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <ErrorBanner message={apiError} />

        <AuthTextField
          id="login"
          label="Username or email"
          type="text"
          autoComplete="username"
          placeholder="username or email"
          error={errors.login?.message}
          registration={register('login')}
        />

        <div>
          <AuthTextField
            id="password"
            label="Password"
            type="password"
            autoComplete="current-password"
            placeholder="********"
            error={errors.password?.message}
            registration={register('password')}
          />
          <div className="mt-1 text-right">
            <Link to="/forgot-password" className="text-sm text-blue-400 hover:text-blue-300">
              Forgot password?
            </Link>
          </div>
        </div>

        <Button type="submit" className="w-full" loading={login.isPending}>
          Sign In
        </Button>
      </form>

      <GoogleAuthSection mode="signin" onError={setApiError} />

      <p className="mt-6 text-center text-sm text-muted">
        Don't have an account?{' '}
        <Link to="/signup" className="text-blue-400 hover:text-blue-300">
          Sign up
        </Link>
      </p>

      <p className="mt-2 text-center text-sm text-muted">
        <Link to="/privacy" className="text-blue-400 hover:text-blue-300">
          Privacy Policy
        </Link>
      </p>
    </AuthLayout>
  )
}
